/*
 * Sfera Boards — US visitor detection
 * ------------------------------------------------------------
 * Shows the US quote notice (top banner + product-page callout) only to
 * visitors detected in the United States, WITHOUT needing the US market
 * enabled in Shopify. Detection uses Shopify's own IP geolocation endpoint
 * (/browsing_context_suggestions.json), so there are no third-party services.
 *
 * Preview / testing (Jorge browses from Spain):
 *   - Add ?us=1 to any URL to force the notice ON  (kept for the whole session)
 *   - Add ?us=0 to any URL to force the notice OFF
 *
 * Elements opted-in with [data-sfera-us] start hidden and are revealed when a
 * US visitor is detected. The top banner (.sfera-us-banner) also remembers if
 * the visitor closed it (localStorage) and stays closed after that.
 */
(function () {
  'use strict';

  var TEST_KEY = 'sfera_us_test';        // sessionStorage: '1' forced on, '0' forced off
  var DETECT_KEY = 'sfera_us_country';   // sessionStorage: cached detected country handle
  var BANNER_DISMISS_KEY = 'sfera_us_banner_dismissed'; // localStorage: '1' if closed

  function safeSession(get, key, value) {
    try {
      return get ? window.sessionStorage.getItem(key) : window.sessionStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  }
  function safeLocalGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeLocalSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) {}
  }

  // 1) Honour the ?us=1 / ?us=0 testing override and remember it for the session.
  function readTestOverride() {
    var match = window.location.search.match(/[?&]us=([01])/);
    if (match) {
      safeSession(false, TEST_KEY, match[1]);
      return match[1];
    }
    return safeSession(true, TEST_KEY);
  }

  // 2) Ask Shopify which country the visitor is browsing from (by IP).
  function detectCountry() {
    var cached = safeSession(true, DETECT_KEY);
    if (cached) return Promise.resolve(cached);

    return fetch('/browsing_context_suggestions.json', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var handle = '';
        if (data && data.detected_values && data.detected_values.country) {
          handle = (data.detected_values.country.handle || '').toUpperCase();
        }
        if (handle) safeSession(false, DETECT_KEY, handle);
        return handle;
      })
      .catch(function () { return ''; });
  }

  // Publish the banner's real height so the sticky header can sit right below it.
  function updateBannerHeight(banner) {
    var b = banner || document.querySelector('.sfera-us-banner');
    if (!b) return;
    document.documentElement.style.setProperty('--sfera-banner-height', b.offsetHeight + 'px');
  }

  // US only: swap the euro footer promo line for a US-friendly brand line.
  // Non-US visitors never run this, so their footer stays exactly as it is.
  function swapFooterPromo() {
    var FROM = '10€ OFF FOR NEWSLETTER SUBSCRIBERS';
    var TO = 'THE FUTURE OF BALANCE TRAINING';
    var els = document.querySelectorAll('body *');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length === 0 && el.textContent.trim() === FROM) {
        // The top announcement bar carries the same text but is hidden for US;
        // leave it, only rebrand the footer instances.
        if (el.closest('[id*="announcement-bar"]')) continue;
        el.textContent = TO;
      }
    }
  }

  function revealNotices() {
    // On the quote page itself the top banner is redundant (it links to this
    // very page), so don't show it there — only elsewhere (home, product, …).
    var onQuotePage = !!document.querySelector('.us-quote-form');

    var els = document.querySelectorAll('[data-sfera-us]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];

      if (el.classList.contains('sfera-us-banner')) {
        if (onQuotePage) continue;
        // The dismissible top banner respects a previous close.
        if (safeLocalGet(BANNER_DISMISS_KEY) === '1') continue;
      }
      el.hidden = false;
      el.classList.add('is-visible');
    }
    document.documentElement.classList.add('sfera-us');

    // US only: rebrand the euro footer promo line (non-US keeps it unchanged).
    swapFooterPromo();

    // Hide the euro "10€ off" promos for US visitors (opt-in via the banner's
    // data-hide-promo flag), so they only see the quote notice.
    var banner = document.querySelector('.sfera-us-banner');
    if (banner && banner.getAttribute('data-hide-promo') === 'true') {
      document.documentElement.classList.add('sfera-hide-promo');

      // Neutralize the theme's newsletter promo popup + its sticky launcher for
      // US visitors. Removing the node alone is NOT enough: the popup's open-timer
      // (showPopupWithDelay) keeps a reference and can still lock body scroll after
      // we detach it, and a lock already applied is never released — it stays as
      // body{overflow:hidden} and freezes the page. So for each popup we first stop
      // any pending open (closed/subscribed flags read inside the timer), then call
      // its own hide() to release the scroll lock precisely (enableBodyScroll on its
      // own dialog holder — cart/menu locks stay untouched), then remove it.
      var promos = document.querySelectorAll(
        '.newsletter-popup, .newsletter-popup__fixed-button, [id^="NewsletterModal"], [id^="NewsletterFixedButton"]'
      );
      for (var p = 0; p < promos.length; p++) {
        var promo = promos[p];
        try {
          promo.closed = 'true';
          promo.subscribed = 'true';
          if (typeof promo.hide === 'function') promo.hide();
        } catch (e) {}
        promo.parentNode && promo.parentNode.removeChild(promo);
      }

      // Safety net: if the popup had already frozen the page and nothing else that
      // legitimately locks scroll is open, restore scrolling for US visitors.
      if (document.body.style.overflow === 'hidden' &&
          !document.querySelector('[role="dialog"][open], newsletter-popup[open], modal-dialog[open], age-verification-popup[open], cart-drawer.active, cart-drawer[open]')) {
        try {
          if (window.bodyScrollLock && typeof bodyScrollLock.clearAllBodyScrollLocks === 'function') {
            bodyScrollLock.clearAllBodyScrollLocks();
          }
        } catch (e) {}
        document.body.style.overflow = '';
      }
    }

    // Keep the banner pinned to the top (unless it was dismissed) and offset the
    // sticky header below it so they stack instead of overlapping.
    if (banner && !banner.hidden) {
      document.documentElement.classList.add('sfera-banner-fixed');
      updateBannerHeight(banner);
    }

    wireBannerDismiss();
  }

  function wireBannerDismiss() {
    var buttons = document.querySelectorAll('[data-sfera-us-dismiss]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        safeLocalSet(BANNER_DISMISS_KEY, '1');
        var banner = this.closest('.sfera-us-banner');
        if (banner) {
          banner.hidden = true;
          banner.classList.remove('is-visible');
        }
        // Un-pin: let the header return to the very top now the banner is gone.
        document.documentElement.classList.remove('sfera-banner-fixed');
        document.documentElement.style.setProperty('--sfera-banner-height', '0px');
      });
    }
  }

  function init() {
    var override = readTestOverride();
    if (override === '1') { revealNotices(); return; }
    if (override === '0') { return; }

    detectCountry().then(function (country) {
      if (country === 'US') revealNotices();
    });
  }

  // Re-measure the banner if the window resizes (its text may wrap on mobile).
  window.addEventListener('resize', function () {
    if (document.documentElement.classList.contains('sfera-banner-fixed')) {
      updateBannerHeight();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
